/**
 * Monta o `ReportData` completo: memoriais, cascata de níveis, Caracterização
 * do recurso, infraestrutura e contexto, para cada Marcação (ou para o estado).
 *
 * Regra de falha (decidida com o produto): o relatório sai mesmo que uma
 * consulta falhe — a seção afetada mostra "dado indisponível". Ele só falha
 * inteiro se não houver nenhum número de geração para nenhum assunto.
 *
 * Tudo que toca o mapa (captura de imagem, legendas do painel) entra por
 * `deps`, para esta orquestração ficar testável sem Leaflet.
 */

import {
  ApiGetProjectionAreaStats,
  ApiGetResourceCharacterization,
  ApiGetRestrictionBreakdown,
  type ProjectionAreaStatsResponse,
  type ResourceCharacterizationResponse,
  type ResourceRegion,
  type RestrictionBreakdownResponse,
} from "../../../services/api";
import {
  EOLICO_PROJECTION_LAYER_OPTIONS,
  PROJECTION_LAYER_OPTIONS,
  REPORT_PROJECTION_EOLICO_LAYER,
  REPORT_PROJECTION_SOLAR_LAYER,
  REPORT_PROJECTION_TYPES,
  type ReportProjectionType,
} from "../../../global/constants/projectionLayers";
import { computeAreaKm2 } from "../../../utils/areaFormatting";
import { getFirstVertexLatLng } from "../../../utils/geo";
import { findPredominantMunicipality } from "../utils/municipalityLookup";
import {
  fetchInfrastructure,
  fetchVocacaoPrincipal,
  toDistanceGeometry,
} from "./infrastructure";
import type {
  CascadeLevel,
  ContextLegend,
  EnergyAnalysis,
  EnergyType,
  MarkingInput,
  ReportConfig,
  ReportData,
  ReportSubject,
  StateReference,
} from "./types";

/** Área oficial de Pernambuco continental, usada no relatório do estado. */
export const PE_MAINLAND_AREA_KM2 = 98_312;

/**
 * Altura do rotor no cálculo eólico do relatório: 100 m, a mesma da
 * Caracterização do recurso e dos rasters de Weibull de referência.
 */
export const REPORT_WIND_HUB_HEIGHT_M = 100;

export interface ReportProgress {
  message: string;
  percentage: number;
}

export interface BuildReportDeps {
  /** Captura o mapa enquadrado na Marcação (ou no estado, com `null`). */
  captureMap: (marking: MarkingInput | null) => Promise<string | null>;
  /** Legendas das camadas de contexto, com contagens dentro do recorte. */
  buildContextLegends: (
    geometry: GeoJSON.Geometry | null
  ) => Promise<ContextLegend[]>;
  onProgress?: (progress: ReportProgress) => void;
}

const LEVEL_LABEL = Object.fromEntries(
  REPORT_PROJECTION_TYPES.map((t) => [t.value, t.label])
) as Record<ReportProjectionType, string>;

/**
 * Níveis da tabela da cascata: todos os que a energia tem (solar vai do
 * Teórico ao Econômico III), não só os três oferecidos como nível detalhado.
 */
const CASCADE_LEVELS: Record<
  EnergyType,
  readonly { label: string; layerName: string }[]
> = {
  solar: PROJECTION_LAYER_OPTIONS,
  eolico: EOLICO_PROJECTION_LAYER_OPTIONS,
};

function layerFor(energy: EnergyType, level: ReportProjectionType): string {
  return energy === "solar"
    ? REPORT_PROJECTION_SOLAR_LAYER[level]
    : REPORT_PROJECTION_EOLICO_LAYER[level];
}

async function settle<T>(promise: Promise<T>): Promise<T | null> {
  try {
    return await promise;
  } catch {
    return null;
  }
}

async function fetchProjectionStats(
  energy: EnergyType,
  layerName: string,
  geometry: GeoJSON.Geometry | null
): Promise<ProjectionAreaStatsResponse | null> {
  const stats = await settle(
    ApiGetProjectionAreaStats({
      layer_name: layerName,
      geometry,
      hub_height_m: energy === "eolico" ? REPORT_WIND_HUB_HEIGHT_M : undefined,
    })
  );
  // No estado inteiro o backend responde zero quando o raster não pôde ser
  // lido — e nenhum nível tem área propícia nula em Pernambuco inteiro. Zero
  // ali é "indisponível", não um resultado.
  if (geometry === null && stats && stats.propitious_area_km2 === 0) return null;
  return stats;
}

function fetchCharacterization(
  energy: EnergyType,
  geometry: GeoJSON.Geometry | null,
  region: ResourceRegion = "pe"
): Promise<ResourceCharacterizationResponse | null> {
  return settle(
    ApiGetResourceCharacterization(
      geometry ? { energy, geometry } : { energy, region }
    )
  );
}

export async function fetchEnergyAnalysis(
  energy: EnergyType,
  level: ReportProjectionType,
  geometry: GeoJSON.Geometry | null
): Promise<EnergyAnalysis> {
  const levels = CASCADE_LEVELS[energy];
  const detailedLayerName = layerFor(energy, level);

  const [cascadeStats, areaMemorial, characterization] = await Promise.all([
    Promise.all(
      levels.map((l) => fetchProjectionStats(energy, l.layerName, geometry))
    ),
    settle<RestrictionBreakdownResponse>(
      ApiGetRestrictionBreakdown({
        layer_name: detailedLayerName,
        geometry,
        include_geometry: false,
      })
    ),
    fetchCharacterization(energy, geometry),
  ]);

  const cascade: CascadeLevel[] = levels.map((l, i) => ({
    layerName: l.layerName,
    label: l.label,
    stats: cascadeStats[i],
  }));
  const detailedLevel =
    cascade.find((c) => c.layerName === detailedLayerName) ?? cascade[0];

  return {
    energy,
    cascade,
    detailedLevel,
    areaMemorial,
    generationMemorial: detailedLevel.stats?.generation_memorial ?? null,
    characterization,
  };
}

async function fetchAnalyses(
  config: ReportConfig,
  geometry: GeoJSON.Geometry | null
): Promise<Partial<Record<EnergyType, EnergyAnalysis>>> {
  const results = await Promise.all(
    config.energies.map((energy) =>
      fetchEnergyAnalysis(energy, config.level, geometry)
    )
  );
  return Object.fromEntries(results.map((a) => [a.energy, a]));
}

async function buildMarkingSubject(
  marking: MarkingInput,
  config: ReportConfig,
  mapImage: string | null
): Promise<ReportSubject> {
  // Os endpoints de relatório só aceitam Polygon. Um município em várias
  // partes é analisado pelo seu maior polígono — em todas as consultas, para
  // que área, memoriais e distâncias falem do mesmo recorte.
  const geometry = toDistanceGeometry(marking.geometry);
  const municipalityPromise =
    marking.kind === "municipio"
      ? Promise.resolve(marking.name)
      : settle(findPredominantMunicipality(geometry));

  const [analyses, infrastructure, municipalityName] = await Promise.all([
    fetchAnalyses(config, geometry),
    settle(fetchInfrastructure(geometry, config.systemMode)),
    municipalityPromise,
  ]);
  const vocacaoPrincipal = municipalityName
    ? await fetchVocacaoPrincipal(municipalityName)
    : null;

  return {
    id: marking.id,
    name: marking.name,
    kind: marking.kind,
    areaKm2: computeAreaKm2(geometry),
    municipalityName: municipalityName ?? null,
    coordinates: marking.kind === "area" ? getFirstVertexLatLng(geometry) : null,
    mapImage,
    analyses,
    infrastructure: infrastructure ?? [],
    vocacaoPrincipal,
  };
}

async function buildStateSubject(
  config: ReportConfig,
  mapImage: string | null
): Promise<ReportSubject> {
  return {
    id: "pernambuco",
    name: "Pernambuco",
    kind: "estado",
    areaKm2: PE_MAINLAND_AREA_KM2,
    municipalityName: null,
    coordinates: null,
    mapImage,
    analyses: await fetchAnalyses(config, null),
    infrastructure: [],
    vocacaoPrincipal: null,
  };
}

async function buildStateReference(config: ReportConfig): Promise<StateReference> {
  const entries = await Promise.all(
    config.energies.map(async (energy) => {
      const [stats, characterization] = await Promise.all([
        fetchProjectionStats(energy, layerFor(energy, config.level), null),
        fetchCharacterization(energy, null),
      ]);
      return [energy, { stats, characterization }] as const;
    })
  );
  return { analyses: Object.fromEntries(entries) };
}

function hasAnyGenerationNumber(subjects: ReportSubject[]): boolean {
  return subjects.some((subject) =>
    Object.values(subject.analyses).some((a) => a?.detailedLevel.stats != null)
  );
}

export class ReportGenerationError extends Error {}

export async function buildReport(
  config: ReportConfig,
  markings: MarkingInput[],
  deps: BuildReportDeps
): Promise<ReportData> {
  const progress = (message: string, percentage: number) =>
    deps.onProgress?.({ message, percentage });

  if (config.scope === "marcacao" && markings.length !== 1) {
    throw new ReportGenerationError("Selecione uma Marcação para o relatório.");
  }
  if (config.scope === "comparativo" && markings.length !== 2) {
    throw new ReportGenerationError(
      "Selecione exatamente duas Marcações para o relatório comparativo."
    );
  }
  if (config.energies.length === 0) {
    throw new ReportGenerationError("Escolha ao menos um tipo de energia.");
  }

  // A captura mexe no mapa (zoom, camadas ocultas), então é sequencial e
  // vem antes das consultas, que rodam em paralelo depois.
  progress("Capturando o mapa de localização...", 5);
  const isState = config.scope === "estado";
  const mapImages: Array<string | null> = [];
  if (isState) {
    mapImages.push(await settle(deps.captureMap(null)));
  } else {
    for (const marking of markings) {
      mapImages.push(await settle(deps.captureMap(marking)));
    }
  }
  // No comparativo as legendas contam o estado inteiro, então o mapa de
  // contexto também precisa ser o do estado, e não o da primeira Marcação.
  const contextMapImage =
    config.includeContextLayers && config.scope === "comparativo"
      ? await settle(deps.captureMap(null))
      : mapImages[0] ?? null;

  progress("Calculando memoriais, recurso e infraestrutura...", 25);
  const subjectsPromise = isState
    ? buildStateSubject(config, mapImages[0]).then((s) => [s])
    : Promise.all(
        markings.map((m, i) => buildMarkingSubject(m, config, mapImages[i]))
      );
  const stateReferencePromise =
    config.scope === "comparativo" ? buildStateReference(config) : null;
  const noronhaPromise = isState
    ? Promise.all(
        config.energies.map(
          async (energy) =>
            [energy, await fetchCharacterization(energy, null, "noronha")] as const
        )
      ).then((entries) => Object.fromEntries(entries))
    : null;

  const [subjects, stateReference, noronha] = await Promise.all([
    subjectsPromise,
    stateReferencePromise,
    noronhaPromise,
  ]);

  if (!hasAnyGenerationNumber(subjects)) {
    throw new ReportGenerationError(
      "Não foi possível calcular o potencial de geração. Tente novamente em instantes."
    );
  }

  let context: ReportData["context"] = null;
  if (config.includeContextLayers) {
    progress("Buscando as camadas de contexto...", 80);
    // No comparativo as contagens das legendas seriam de uma área só, o que
    // confunde; ali elas valem para o estado, como `contextMapImage`.
    const legendGeometry =
      config.scope === "marcacao" ? toDistanceGeometry(markings[0].geometry) : null;
    const legends = await settle(deps.buildContextLegends(legendGeometry));
    context = { mapImage: contextMapImage, legends: legends ?? [] };
  }

  progress("Montando o relatório...", 95);
  return {
    metadata: {
      projectName: config.projectName,
      projectDescription: config.projectDescription,
      timestamp: new Date().toISOString(),
    },
    scope: config.scope,
    energies: config.energies,
    levelLabel: LEVEL_LABEL[config.level],
    systemMode: config.systemMode,
    subjects,
    stateReference,
    noronha,
    context,
  };
}
